// Wrapper around localStorage with optional XOR-based obfuscation.
//
// THREAT MODEL: this is obfuscation, not security. The XOR key ships in the
// bundle; anyone with DevTools can extract it and reverse the encoding. The
// purpose is to raise the bar against casual hand-editing of localStorage
// values (e.g. a player rewriting their coin balance from 100 to 99999),
// not to defend against a determined attacker.
//
// Whether new writes are obfuscated is controlled by the build-time
// STORAGE_ENCRYPTED flag (see scripts/generate-version.js): true on CI deploys
// of main, false everywhere else (local dev, manual builds, PR builds).
//
// Reads always auto-detect the format via the `e1:` prefix, so:
//   - Legacy clear values written by older builds keep working.
//   - localStorage copied from prod can be inspected in dev (the dev build
//     can decode prod values; it just writes new ones in clear text).

import { STORAGE_ENCRYPTED } from './version.js';

const XOR_KEY = 'pmq-store-2026';
const PREFIX  = 'e1:';

function xorBytes( bytes )
{
    const out = new Uint8Array( bytes.length );
    for ( let i = 0; i < bytes.length; i++ )
    {
        out[ i ] = bytes[ i ] ^ XOR_KEY.charCodeAt( i % XOR_KEY.length );
    }
    return out;
}

function bytesToBase64( bytes )
{
    let bin = '';
    for ( let i = 0; i < bytes.length; i++ ) bin += String.fromCharCode( bytes[ i ] );
    return btoa( bin );
}

function base64ToBytes( b64 )
{
    const bin = atob( b64 );
    const bytes = new Uint8Array( bin.length );
    for ( let i = 0; i < bin.length; i++ ) bytes[ i ] = bin.charCodeAt( i );
    return bytes;
}

function obfuscate( plain )
{
    return PREFIX + bytesToBase64( xorBytes( new TextEncoder().encode( plain ) ) );
}

function deobfuscate( encoded )
{
    try
    {
        return new TextDecoder().decode( xorBytes( base64ToBytes( encoded.slice( PREFIX.length ) ) ) );
    }
    catch
    {
        return null;
    }
}

export function getItem( key )
{
    const raw = localStorage.getItem( key );
    if ( raw === null ) return null;
    if ( raw.startsWith( PREFIX ) ) return deobfuscate( raw );
    return raw;
}

export function setItem( key, value )
{
    const str = String( value );
    localStorage.setItem( key, STORAGE_ENCRYPTED ? obfuscate( str ) : str );
}

export function removeItem( key )
{
    localStorage.removeItem( key );
}
